import { EWorldMapInitialType } from '__generated__/globalTypes';
import { WorldMap_worldMap } from 'graphql/query/__generated__/WorldMap';
import { WorldMapView_worldMapView } from 'graphql/query/__generated__/worldMapView';
import { boundCoordinateToEdges } from 'libs/utils/world/worldUtils';
import { LngLat, LngLatBounds } from 'mapbox-gl';
import { ICoordinates } from 'react-give-me-map';

export type InitialBoundsData = {
    topLeft: ICoordinates;
    bottomRight: ICoordinates;
    padding?: number;
};

export const getBoundsOffsets = (bounds: InitialBoundsData) => {
    const lat = ((bounds.padding ?? 0) / 100) * (bounds.bottomRight.lat - bounds.topLeft.lat);
    const lng = ((bounds.padding ?? 0) / 100) * (bounds.bottomRight.lng - bounds.topLeft.lng);

    return { lat, lng };
};

export const areBoundsValid = (bounds: { bottomRight: ICoordinates; topLeft: ICoordinates }) => {
    return Boolean(
        bounds &&
            bounds.bottomRight &&
            bounds.topLeft &&
            bounds.bottomRight.lat &&
            bounds.bottomRight.lng &&
            bounds.topLeft.lat &&
            bounds.topLeft.lng
    );
};

const getBounds = (mapData: WorldMap_worldMap | WorldMapView_worldMapView, isWidePage: boolean) => {
    const isAutomatic = mapData.initialType === EWorldMapInitialType.AUTOMATIC;

    if (isAutomatic) {
        return mapData.initial.autoBounds;
    }

    if (isWidePage) {
        return mapData.initial.bounds;
    }

    if (mapData.initialMobile.bounds && areBoundsValid(mapData.initialMobile.bounds)) {
        return mapData.initialMobile.bounds;
    }

    mapData.initial.bounds;
};

const getCenter = (mapData: WorldMap_worldMap | WorldMapView_worldMapView, isWidePage: boolean) => {
    if (isWidePage) {
        return { lat: mapData.initial.lat, lng: mapData.initial.lng, zoom: mapData.initial.zoom };
    }

    if (mapData.initialMobile.lat && mapData.initialMobile.lng && mapData.initialMobile.zoom) {
        return {
            lat: mapData.initialMobile.lat,
            lng: mapData.initialMobile.lng,
            zoom: mapData.initialMobile.zoom
        };
    }

    return { lat: mapData.initial.lat, lng: mapData.initial.lng, zoom: mapData.initial.zoom };
};

export const getInitialBoundsAndCenter = (mapData: WorldMap_worldMap, isWidePage: boolean) => {
    const bounds = getBounds(mapData, isWidePage);

    if (areBoundsValid(bounds)) {
        const geoBounds = new LngLatBounds(
            new LngLat(bounds.topLeft.lng, bounds.bottomRight.lat),
            new LngLat(bounds.bottomRight.lng, bounds.topLeft.lat)
        );
        return {
            bounds: geoBounds,
            latitude: null,
            longitude: null
        };
    }

    return null;
};

export const getInitialViewState = (
    mapData: WorldMap_worldMap | WorldMapView_worldMapView,
    isWidePage: boolean
) => {
    if (mapData.initial.useBounds) {
        const bounds = getBounds(mapData, isWidePage);
        if (areBoundsValid(bounds)) {
            const isAutomatic = mapData.initialType === EWorldMapInitialType.AUTOMATIC;

            const automaticOffsets = getBoundsOffsets(bounds);

            const finalBottomLeft = boundCoordinateToEdges({
                lng: bounds.topLeft.lng - (isAutomatic ? automaticOffsets.lng : 0),
                lat: bounds.bottomRight.lat + (isAutomatic ? automaticOffsets.lat : 0)
            });

            const finalTopRight = boundCoordinateToEdges({
                lng: bounds.bottomRight.lng + (isAutomatic ? automaticOffsets.lng : 0),
                lat: bounds.topLeft.lat - (isAutomatic ? automaticOffsets.lat : 0)
            });

            const geoBounds = new LngLatBounds(
                new LngLat(finalBottomLeft.lng, finalBottomLeft.lat),
                new LngLat(finalTopRight.lng, finalTopRight.lat)
            );

            return {
                bounds: geoBounds
            };
        }
    } else {
        const center = getCenter(mapData, isWidePage);
        return {
            latitude: center.lat,
            longitude: center.lng,
            zoom: center.zoom
        };
    }
};
